import { useEffect, RefObject, useRef } from "react";

/**
 * Utility for managing click outside event listeners
 * Fixes the typo issue where removeEventListener didn't match addEventListener
 * Uses ref to store handler to avoid re-attaching listeners on every handler change
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: () => void
) => {
  // Store handler in ref to avoid re-attaching listeners when handler changes
  const handlerRef = useRef(handler);
  
  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handlerRef.current();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]); // Only depend on ref, handler is accessed via ref
};

// For use outside React components
export const createClickOutsideHandler = (
  element: HTMLElement,
  handler: () => void
) => {
  const handleClickOutside = (event: MouseEvent) => {
    if (element && !element.contains(event.target as Node)) {
      handler();
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
};

